import { lazy } from 'react';
import { createBrowserRouter, Navigate } from 'react-router-dom';
import { RootLayout } from './components/RootLayout';
import { PublicLayout } from './components/PublicLayout';
import { RequireAdmin } from './components/RequireAdmin';
import { HomePage } from './pages/HomePage';
import { UpdatePage } from './pages/UpdatePage';
import { EventPage } from './pages/EventPage';
import { PhotosPage } from './pages/PhotosPage';
import { GuestbookPage } from './pages/GuestbookPage';
import { StayPage } from './pages/StayPage';
import { ConfirmationPage } from './pages/ConfirmationPage';
import { DonatePage } from './pages/DonatePage';
import { ContactPage } from './pages/ContactPage';
import { PrivacyPage, TermsPage } from './pages/LegalPages';
import { NotFoundPage } from './pages/NotFoundPage';
import { AdminLoginPage } from './admin/AdminLoginPage';

// The RSVP and the admin panel are lazy so the public landing pages stay light.
// Checkout is gone: the hotel block was cancelled on 2026-09-15 and this site
// no longer sells anything. The room catalogue, the room detail page and the
// checkout live on the keep/springhill-room-booking branch if they are ever
// needed again; /confirmation/:code stays so the links already in people's
// inboxes still resolve while the refunds go through.
const page = <T extends Record<string, unknown>>(load: () => Promise<T>, key: keyof T) =>
  lazy(() => load().then((m) => ({ default: m[key] as React.ComponentType })));

const RsvpPage = page(() => import('./pages/rsvp/RsvpPage'), 'RsvpPage');
const RsvpConfirmationPage = page(
  () => import('./pages/rsvp/RsvpConfirmationPage'),
  'RsvpConfirmationPage',
);
const AdminLayout = page(() => import('./admin/AdminLayout'), 'AdminLayout');
const AdminDashboardPage = page(() => import('./admin/AdminDashboardPage'), 'AdminDashboardPage');
const AdminRoomsPage = page(() => import('./admin/rooms/AdminRoomsPage'), 'AdminRoomsPage');
const AdminRoomEditPage = page(
  () => import('./admin/rooms/AdminRoomEditPage'),
  'AdminRoomEditPage',
);
const AdminAmenitiesPage = page(() => import('./admin/AdminAmenitiesPage'), 'AdminAmenitiesPage');
const AdminReservationsPage = page(
  () => import('./admin/reservations/AdminReservationsPage'),
  'AdminReservationsPage',
);
const AdminReservationDetailPage = page(
  () => import('./admin/reservations/AdminReservationDetailPage'),
  'AdminReservationDetailPage',
);
const AdminSettingsPage = page(
  () => import('./admin/settings/AdminSettingsPage'),
  'AdminSettingsPage',
);
const AdminActivityPage = page(() => import('./admin/AdminActivityPage'), 'AdminActivityPage');
const AdminUsersPage = page(() => import('./admin/AdminUsersPage'), 'AdminUsersPage');
const AdminGuestbookPage = page(() => import('./admin/AdminGuestbookPage'), 'AdminGuestbookPage');
const AdminRsvpsPage = page(() => import('./admin/rsvp/AdminRsvpsPage'), 'AdminRsvpsPage');
const AdminRsvpDetailPage = page(
  () => import('./admin/rsvp/AdminRsvpDetailPage'),
  'AdminRsvpDetailPage',
);
const AdminDoorListPage = page(() => import('./admin/rsvp/AdminDoorListPage'), 'AdminDoorListPage');

export const router = createBrowserRouter([
  {
    element: <RootLayout />,
    children: [
      {
        element: <PublicLayout />,
        children: [
          { index: true, element: <HomePage /> },
          // The tribute page is parked on the keep/about-brandon-and-testimonials
          // branch; shared links keep working while it is away.
          { path: 'about', element: <Navigate to="/" replace /> },
          { path: 'update', element: <UpdatePage /> },
          { path: 'event', element: <EventPage /> },
          { path: 'photos', element: <PhotosPage /> },
          { path: 'guestbook', element: <GuestbookPage /> },
          // /rooms is now "Where to stay" and points at SecretsOverflow. The
          // per-room and checkout URLs were shared and printed, so they land on
          // it rather than on a 404.
          { path: 'rooms', element: <StayPage /> },
          { path: 'rooms/:slug', element: <Navigate to="/rooms" replace /> },
          { path: 'checkout/:slug', element: <Navigate to="/rooms" replace /> },
          { path: 'confirmation/:code', element: <ConfirmationPage /> },
          { path: 'rsvp', element: <RsvpPage /> },
          { path: 'rsvp/confirmation/:code', element: <RsvpConfirmationPage /> },
          // The short-lived tickets link points at the RSVP now.
          { path: 'tickets', element: <Navigate to="/rsvp" replace /> },
          { path: 'donate', element: <DonatePage /> },
          { path: 'contact', element: <ContactPage /> },
          { path: 'terms', element: <TermsPage /> },
          { path: 'privacy', element: <PrivacyPage /> },
          { path: '*', element: <NotFoundPage /> },
        ],
      },
      { path: 'admin/login', element: <AdminLoginPage /> },
      {
        path: 'admin',
        element: (
          <RequireAdmin>
            <AdminLayout />
          </RequireAdmin>
        ),
        children: [
          { index: true, element: <AdminDashboardPage /> },
          { path: 'rooms', element: <AdminRoomsPage /> },
          { path: 'rooms/:id', element: <AdminRoomEditPage /> },
          { path: 'amenities', element: <AdminAmenitiesPage /> },
          { path: 'reservations', element: <AdminReservationsPage /> },
          { path: 'reservations/:id', element: <AdminReservationDetailPage /> },
          { path: 'rsvps', element: <AdminRsvpsPage /> },
          { path: 'rsvps/door-list', element: <AdminDoorListPage /> },
          { path: 'rsvps/:id', element: <AdminRsvpDetailPage /> },
          { path: 'settings', element: <AdminSettingsPage /> },
          { path: 'guestbook', element: <AdminGuestbookPage /> },
          { path: 'activity', element: <AdminActivityPage /> },
          { path: 'users', element: <AdminUsersPage /> },
        ],
      },
    ],
  },
]);
